import React from "react";
import { fieldCls } from "components/InlineTable";
import { FaCheckCircle, FaLock, FaRegClock, FaRegEdit, FaRegEye, FaStar } from "react-icons/fa";
import {
  FeedbackStatus,
  MAX_RATING,
  REQUIRED_TOTAL_WEIGHTAGE,
} from "services/Feedback/feedbackInterface";

/**
 * Small presentational pieces shared across the Quarterly Feedback screens.
 *
 * NOTE: grouped in one file rather than one-file-per-tiny-component because
 * each is a few lines and they are always imported together. Every piece takes
 * an exported props type so call sites are type-checked.
 *
 * All colours come from Tailwind theme scales already used elsewhere in the app
 * (emerald = done, amber = waiting, slate = draft) — no new tokens.
 */

// ── Shared field chrome ──────────────────────────────────────────────────────

/**
 * Label and inline-error styling for the feedback forms.
 *
 * NOTE: exported here rather than re-declared per file — the identical strings
 * had been copied into both goal editors, so a tweak to one silently diverged
 * from the other. Pairs with `fieldCls` from components/InlineTable, which
 * supplies the field appearance itself.
 */
export const FIELD_LABEL_CLS = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";
export const FIELD_ERROR_CLS = "mt-1 text-xs font-medium text-error leading-tight";

// ── Status badge ─────────────────────────────────────────────────────────────

export interface FeedbackStatusBadgeProps {
  status?: FeedbackStatus | null;
  /** Shown when `status` is null — a sheet the employee has not started. */
  emptyLabel?: string;
  className?: string;
}

/** Visual treatment per status. Icon + label, never colour alone. */
const STATUS_STYLES: Record<FeedbackStatus, { label: string; cls: string; Icon: React.ElementType }> = {
  DRAFT: {
    label: "Draft",
    cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600",
    Icon: FaRegEdit,
  },
  SUBMITTED: {
    label: "Awaiting approval",
    cls: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700",
    Icon: FaRegClock,
  },
  APPROVED: {
    label: "Approved",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700",
    Icon: FaCheckCircle,
  },
};

export const FeedbackStatusBadge: React.FC<FeedbackStatusBadgeProps> = ({
  status,
  emptyLabel = "Not started",
  className = "",
}) => {
  if (!status) {
    return (
      <span
        className={`inline-flex items-center rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-400 dark:border-gray-600 dark:text-gray-500 ${className}`}
      >
        {emptyLabel}
      </span>
    );
  }

  const { label, cls, Icon } = STATUS_STYLES[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls} ${className}`}
    >
      <Icon size={11} aria-hidden="true" />
      {label}
    </span>
  );
};

// ── Kind tag ─────────────────────────────────────────────────────────────────

export interface FeedbackKindTagProps {
  /** True when the record carries a `selfFeedbackId` — it approved a sheet. */
  isApproval: boolean;
  period?: string;
}

/**
 * Distinguishes a reviewer's approval comment from standalone feedback.
 *
 * NOTE: without it the two are indistinguishable in the list, since both are
 * MANAGER records.
 *
 * CHANGE: the standalone label was "Direct feedback"; it is now just
 * "Feedback". "Direct" was drawing a contrast with nothing a reader could see
 * — the only other value of this tag names an approval outright.
 */
export const FeedbackKindTag: React.FC<FeedbackKindTagProps> = ({ isApproval, period }) => (
  <span
    className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border ${
      isApproval
        ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
        : "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800"
    }`}
  >
    {isApproval ? `Approval of ${period ?? "self feedback"}` : "Feedback"}
  </span>
);

// ── Visibility tag ───────────────────────────────────────────────────────────

export interface FeedbackVisibilityTagProps {
  /** `visibleToEmployee === true`. */
  shared: boolean;
}

/**
 * Whether the employee can see this manager feedback.
 *
 * CHANGE: new. Manager feedback is PRIVATE by default under the updated
 * contract, so an author looking at their own note has no way of knowing
 * whether the employee has read it — or will ever see it — unless the card
 * says so.
 *
 * NOTE: deliberately quieter than the status badge. Private is the state worth
 * noticing, so it carries the stronger colour; shared is reassurance, not an
 * alert. Icon + word, never colour alone.
 */
export const FeedbackVisibilityTag: React.FC<FeedbackVisibilityTagProps> = ({ shared }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-medium border ${
      shared
        ? "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600"
        : "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-500"
    }`}
    title={
      shared
        ? "The employee can see this feedback"
        : "Only you and admins can see this; the employee cannot"
    }
  >
    {shared ? <FaRegEye size={10} aria-hidden="true" /> : <FaLock size={10} aria-hidden="true" />}
    {shared ? "Shared with employee" : "Private"}
  </span>
);

// ── Rating ───────────────────────────────────────────────────────────────────

export interface RatingDisplayProps {
  value?: number | null;
  /** Compact form prints "4/5" without stars — used inside dense table cells. */
  compact?: boolean;
}

export const RatingDisplay: React.FC<RatingDisplayProps> = ({ value, compact = false }) => {
  if (value === null || value === undefined) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">No rating</span>;
  }

  if (compact) {
    return (
      <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-white">
        {value}/{MAX_RATING}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" title={`${value} out of ${MAX_RATING}`}>
      {Array.from({ length: MAX_RATING }, (_, i) => (
        <FaStar
          key={i}
          size={12}
          className={i < value ? "text-amber-400" : "text-gray-200 dark:text-gray-600"}
          aria-hidden="true"
        />
      ))}
      <span className="ml-1 text-xs font-medium tabular-nums text-gray-600 dark:text-gray-300">
        {value}/{MAX_RATING}
      </span>
    </span>
  );
};

export interface RatingSelectProps {
  value?: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  id?: string;
}

/**
 * Rating picker.
 *
 * NOTE: a native `<select>` rather than clickable stars — ratings are optional
 * everywhere, and a select is the only control that can be cleared back to "no
 * rating" without a second affordance.
 *
 * CHANGE: now styled with `fieldCls` — the app's shared field appearance, the
 * same one KraAreaSelect, KraObjectiveSelect and every inline input already
 * use. It previously carried its own classes (`px-3 py-2` / `px-2 py-1.5` and
 * `bg-white`), so on a goal card it rendered visibly SHORTER than the selects
 * beside it and on a different background. The `compact` prop went with them:
 * it existed only to squeeze this control into the old table row, and having
 * two heights was the defect.
 */
export const RatingSelect: React.FC<RatingSelectProps> = ({
  value,
  onChange,
  disabled = false,
  id,
}) => (
  <select
    id={id}
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    aria-label="Rating out of 5"
    className={fieldCls(false)}
  >
    <option value="">—</option>
    {Array.from({ length: MAX_RATING }, (_, i) => i + 1).map((n) => (
      <option key={n} value={n}>
        {n}
      </option>
    ))}
  </select>
);

// ── Weightage meter ──────────────────────────────────────────────────────────

export interface WeightageMeterProps {
  total: number;
  /** Hides the "must total 100%" hint once the sheet is locked. */
  readOnly?: boolean;
}

/**
 * Live total of the goal rows' weightage.
 *
 * NOTE: the server rejects a submit unless this is exactly 100, and the error
 * only arrives after a round trip. Showing the running total (and how far off
 * it is) turns that into something the user fixes before pressing Submit.
 */
export const WeightageMeter: React.FC<WeightageMeterProps> = ({ total, readOnly = false }) => {
  const isComplete = total === REQUIRED_TOTAL_WEIGHTAGE;
  const isOver = total > REQUIRED_TOTAL_WEIGHTAGE;
  const filledPercent = Math.min((total / REQUIRED_TOTAL_WEIGHTAGE) * 100, 100);

  const barColour = isComplete ? "bg-emerald-500" : isOver ? "bg-rose-500" : "bg-primary";
  const textColour = isComplete
    ? "text-emerald-600 dark:text-emerald-400"
    : isOver
      ? "text-rose-600 dark:text-rose-400"
      : "text-gray-600 dark:text-gray-300";

  const remaining = REQUIRED_TOTAL_WEIGHTAGE - total;

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-[80px]">
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
          role="progressbar"
          aria-valuenow={total}
          aria-valuemin={0}
          aria-valuemax={REQUIRED_TOTAL_WEIGHTAGE}
          aria-label="Total weightage"
        >
          <div
            className={`h-full rounded-full transition-all duration-300 ${barColour}`}
            style={{ width: `${filledPercent}%` }}
          />
        </div>
      </div>
      <span className={`whitespace-nowrap text-xs font-semibold tabular-nums ${textColour}`}>
        {total}% {isComplete && <FaCheckCircle size={10} className="inline -mt-0.5" aria-hidden="true" />}
      </span>
      {!readOnly && !isComplete && (
        <span className="whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
          {isOver ? `${Math.abs(remaining)}% over` : `${remaining}% left`}
        </span>
      )}
    </div>
  );
};

// ── Empty state ──────────────────────────────────────────────────────────────

export interface FeedbackEmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

/** Shared empty state so every tab reads the same when it has nothing. */
export const FeedbackEmptyState: React.FC<FeedbackEmptyStateProps> = ({
  title,
  description,
  action,
}) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 px-6 py-10 text-center dark:border-gray-700">
    <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
    {description && (
      <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">{description}</p>
    )}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
